import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CheckSquare, Plus, Loader2, Trash2, Calendar,
  ChevronRight, ExternalLink, Flag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const PRIORITY_STYLES = {
  low: "bg-slate-100 text-slate-600",
  medium: "bg-amber-100 text-amber-600",
  high: "bg-orange-100 text-orange-600",
  urgent: "bg-red-100 text-red-600",
};

export default function MailTasks() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [taskDialog, setTaskDialog] = useState(false);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      const res = await axios.get(`${API}/mail/tasks`, {
        params, headers: getHeaders(), withCredentials: true,
      });
      setTasks(res.data.tasks || []);
    } catch {}
    setLoading(false);
  };

  const handleCreateTask = async (data) => {
    try {
      await axios.post(`${API}/mail/tasks`, data, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("Task created");
      setTaskDialog(false);
      fetchTasks();
    } catch {
      toast.error("Failed to create task");
    }
  };

  const handleUpdateStatus = async (taskId, status) => {
    try {
      await axios.put(`${API}/mail/tasks/${taskId}`, { status }, {
        headers: getHeaders(), withCredentials: true,
      });
      fetchTasks();
    } catch {}
  };

  const handleDelete = async (taskId) => {
    try {
      await axios.delete(`${API}/mail/tasks/${taskId}`, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("Task deleted");
      fetchTasks();
    } catch {}
  };

  const filteredTasks = filter === "all" ? tasks : tasks.filter((t) => t.status === filter);

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-slate-800">Mail Tasks</h2>
          <span className="text-xs text-slate-400">{tasks.length} tasks</span>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-white border border-slate-200 text-xs">
            {["all", "todo", "in_progress", "done"].map((s) => (
              <button key={s} onClick={() => { setFilter(s); }}
                className={`px-2 py-1.5 ${filter === s ? "bg-violet-50 text-violet-700 font-medium" : "text-slate-500 hover:text-slate-700"}`}
              >
                {s === "all" ? "All" : s === "in_progress" ? "In Progress" : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>

          <Dialog open={taskDialog} onOpenChange={setTaskDialog}>
            <DialogTrigger asChild>
              <Button size="sm" className="h-8 text-xs rounded-none bg-violet-600 hover:bg-violet-500">
                <Plus className="w-3 h-3 mr-1" /> New Task
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader>
                <DialogTitle className="text-sm">New Task</DialogTitle>
              </DialogHeader>
              <TaskForm onSubmit={handleCreateTask} onCancel={() => setTaskDialog(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Task list */}
      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-violet-500 mx-auto py-8" />
      ) : filteredTasks.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-sm">No tasks found</div>
      ) : (
        <div className="bg-white border border-slate-200 divide-y divide-slate-100">
          {filteredTasks.map((task) => (
            <div key={task.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-app">
              <input
                type="checkbox"
                checked={task.status === "done"}
                onChange={() => handleUpdateStatus(task.id, task.status === "done" ? "todo" : "done")}
                className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${task.status === "done" ? "line-through text-slate-300" : "text-slate-800"}`}>
                    {task.title}
                  </span>
                  <Badge className={`text-[10px] px-1.5 py-0 border-none ${PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.medium}`}>
                    {task.priority}
                  </Badge>
                </div>
                {task.email_id && (
                  <button onClick={() => navigate(`/comms-centre/mail/detail/${task.email_id}`)}
                    className="flex items-center gap-1 text-[10px] text-violet-500 hover:text-violet-700 mt-0.5"
                  >
                    <ExternalLink className="w-2.5 h-2.5" /> View email
                  </button>
                )}
                <div className="flex items-center gap-2 mt-0.5">
                  {task.due_date && (
                    <span className="flex items-center gap-1 text-[10px] text-slate-400">
                      <Calendar className="w-2.5 h-2.5" />
                      {new Date(task.due_date).toLocaleDateString("en-ZA")}
                    </span>
                  )}
                  {task.enhanced_task_id && (
                    <Badge className="text-[9px] px-1 py-0 bg-green-100 text-green-700 border-none">
                      Linked to SMS
                    </Badge>
                  )}
                </div>
              </div>
              <button onClick={() => handleDelete(task.id)} className="text-slate-300 hover:text-red-500">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TaskForm({ onSubmit, onCancel }) {
  const [data, setData] = useState({ title: "", description: "", due_date: "", priority: "medium" });
  return (
    <div className="space-y-3">
      <Input placeholder="Task title" value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })}
        className="h-8 text-xs rounded-none" />
      <Input placeholder="Description (optional)" value={data.description} onChange={(e) => setData({ ...data, description: e.target.value })}
        className="h-8 text-xs rounded-none" />
      <Input type="date" value={data.due_date} onChange={(e) => setData({ ...data, due_date: e.target.value })}
        className="h-8 text-xs rounded-none" />
      <Select value={data.priority} onValueChange={(v) => setData({ ...data, priority: v })}>
        <SelectTrigger className="h-8 text-xs rounded-none">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="rounded-none">
          <SelectItem value="low" className="text-xs">Low</SelectItem>
          <SelectItem value="medium" className="text-xs">Medium</SelectItem>
          <SelectItem value="high" className="text-xs">High</SelectItem>
          <SelectItem value="urgent" className="text-xs">Urgent</SelectItem>
        </SelectContent>
      </Select>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} className="h-8 text-xs rounded-none">Cancel</Button>
        <Button size="sm" onClick={() => onSubmit(data)} className="h-8 text-xs rounded-none bg-violet-600" disabled={!data.title.trim()}>Create</Button>
      </div>
    </div>
  );
}
